import * as React from 'react';
import { useState } from 'react';
import { TTaskFile } from '../../types/Tasks';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { fetchWithAuthentication } from '../../actions/BaseAction';
import ImageUploader from '../ImageUploader';

const SlideTransition = React.forwardRef(function SlideTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ITaskFilesSectionProps {
  files: TTaskFile[];
  onUploadFile: (filename: string, content: string) => void;
  onDeleteFile: (fileId: number) => void;
  readOnly?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      border: '2px dashed #ccc',
      borderRadius: 8,
      padding: theme.spacing(1),
    },
    fileList: {
      padding: 0,
    },
    fileItem: {
    },
    fileDivider: {
      borderBottom: '1px dashed #ccc',
    },
    fileName: {
      '& .MuiListItemText-primary': {
        color: theme.palette.primary.main,
      },
    },
    uploadArea: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1, 2),
    },
    cameraIcon: {
      width: 40,
      height: 40,
      borderRadius: '50%',
      backgroundColor: '#4caf50',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    sizeNote: {
      color: theme.palette.text.secondary,
      fontSize: '0.75rem',
      marginTop: theme.spacing(0.5),
    },
  })
);

const TaskFilesSection: React.FC<ITaskFilesSectionProps> = ({
  files,
  onUploadFile,
  onDeleteFile,
  readOnly = false,
}): JSX.Element => {
  const classes = useStyles({});
  const { t } = useTranslation();
  const [deleteFileId, setDeleteFileId] = useState<number | null>(null);

  const handleConfirmDelete = () => {
    if (deleteFileId !== null) {
      onDeleteFile(deleteFileId);
      setDeleteFileId(null);
    }
  };

  const handleFileClick = async (file: TTaskFile) => {
    const blob = await fetchWithAuthentication(file.url);
    if (blob) {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    }
  };

  return (
    <Box>
      <Box className={classes.container}>
        {files.length > 0 && (
          <>
            <List className={classes.fileList}>
              {files.map((file) => (
                <ListItem
                  key={file.id}
                  button
                  className={classes.fileItem}
                  onClick={() => handleFileClick(file)}
                >
                  <ListItemIcon>
                    <DescriptionIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.filename}
                    className={classes.fileName}
                    primaryTypographyProps={{ noWrap: true }}
                  />
                  {file.isOwnFile && !readOnly && (
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteFileId(file.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
            <div className={classes.fileDivider} />
          </>
        )}

        {!readOnly && (
          <>
            <ImageUploader
              handlePhotoUpload={onUploadFile}
              accept="*/*"
              wrapperStyle={{ display: 'inline-block' }}
            >
              <Box className={classes.uploadArea}>
                {isMobile ? (
                  <div className={classes.cameraIcon}>
                    <CameraAltIcon style={{ color: '#fff', fontSize: '1.25rem' }} />
                  </div>
                ) : (
                  <Button variant="outlined" size="small" component="span">
                    {t('tasks.detail.upload_files')}
                  </Button>
                )}
              </Box>
            </ImageUploader>
            <Typography className={classes.sizeNote}>
              {t('tasks.detail.file_size_limit')}
            </Typography>
          </>
        )}
      </Box>
      <Dialog
        open={deleteFileId !== null}
        TransitionComponent={SlideTransition}
        onClose={() => setDeleteFileId(null)}
      >
        <DialogTitle>{t('tasks.detail.delete_file_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('tasks.detail.delete_warning')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFileId(null)} color="primary">
            {t('tasks.detail.disagree')}
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary">
            {t('tasks.detail.agree')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskFilesSection;
